import type { ReactNode } from "react";
import type { SubscriptionView } from "../api/accountApi";
import { useAccountAuth } from "../contexts/AccountAuthContext";
import { formatDay, limitLine, productLabel, subscriptionStatus } from "../lib/accountFormat";
import { Facts, Status, Steps } from "./Portal";
import { Seal } from "./Seal";

/** The membership as one sealed document: product, state, key dates and the next action. */
export function MembershipDocument({
  sub,
  version,
  actions,
}: {
  sub: SubscriptionView;
  version?: string | null;
  actions: ReactNode;
}) {
  const { sub: accountId, email, createdAt } = useAccountAuth();
  const seed = accountId || email || "capital-pilot";
  const ending = sub.entitled && sub.cancel_at_period_end;
  const pastDue = sub.status === "past_due";
  const limits = sub.products.find((product) => product.code === sub.product_code)?.limits;

  return (
    <article className={sub.entitled ? "document" : "document document--open"} aria-label="Mitgliedschaft">
      <div className="document__body">
        <div className="document__title">
          <h2>{sub.entitled ? productLabel(sub.product_code) : "Noch keine Mitgliedschaft"}</h2>
          {sub.entitled ? (
            <Status tone={pastDue ? "warn" : ending ? "off" : "ok"}>
              {ending ? "Gekündigt" : subscriptionStatus(sub.status, sub.entitled)}
            </Status>
          ) : null}
        </div>

        {sub.entitled ? (
          <Facts
            items={[
              sub.current_period_end
                ? {
                    label: ending ? "Endet am" : "Nächste Abrechnung",
                    value: <span className="data">{formatDay(sub.current_period_end)}</span>,
                  }
                : null,
              limits
                ? {
                    label: "Umfang",
                    value: `${limitLine(limits.properties, "Immobilie", "Immobilien")} · ${limitLine(limits.bank_accounts, "Bankkonto", "Bankkonten")}`,
                  }
                : null,
              createdAt ? { label: "Mitglied seit", value: <span className="data">{formatDay(createdAt)}</span> } : null,
              version ? { label: "Desktop-Version", value: <span className="data">{version}</span> } : null,
              accountId ? { label: "Konto-ID", value: <span className="data data--id">{accountId}</span> } : null,
              sub.ai_lotse.entitled
                ? { label: "KI-Lotse", value: sub.ai_lotse.cancel_at_period_end ? "Gekündigt" : "Gebucht" }
                : null,
            ]}
          />
        ) : (
          <>
            <p className="document__lead">
              Wählen Sie Capital Pilot oder Capital Pilot Advisor. Danach liegt der Installer bereit.
            </p>
            <Steps current={2} />
          </>
        )}

        <div className="document__actions">{actions}</div>
      </div>

      <Seal
        className="document__seal"
        seed={seed}
        state={!sub.entitled ? "open" : ending ? "ending" : "active"}
      />
    </article>
  );
}
